import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, NgZone, Renderer2, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { MainLayoutService } from '../../../../services/main-layout.service';
import { UsersProfileService } from '../../../../services/api/users-profile.service';
import { AlertNotificationService } from '../../../../services/alert-notification.service';
import { AuthService } from '../../../../services/api/auth.service';
import { StoreService } from '../../../../services/store.service';
import { UserService } from '../../../../services/api/user.service';
import { RouteService } from '../../../../services/route.service';

@Component({
  selector: '[main-header]',
  imports: [CommonModule],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss'
})
export class HeaderComponent implements OnInit, OnDestroy {
  user: any = null;
  selectedProfile: any = null;
  logoUrl: string = '/assets/logo/logo_gateway.png';
  impersonation: { userId: string; firstName?: string; lastName?: string; auth: any } | null = null;
  private subscription: Subscription = new Subscription();

  constructor(
    private renderer: Renderer2,
    private router: Router,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    protected mainLayoutService: MainLayoutService,
    private usersProfileService: UsersProfileService,
    private alertNotificationService: AlertNotificationService,
    private authService: AuthService,
    private storeService: StoreService,
    private userService: UserService,
    private routeService: RouteService
  ) { }

  ngOnInit(): void {
    this.subscription.add(
      this.mainLayoutService.logoUrl$.subscribe(url => {
        if (url && url !== this.logoUrl) {
          const img = new Image();
          img.onload = () => this.ngZone.run(() => { this.logoUrl = url; this.cdr.detectChanges(); });
          img.onerror = () => {  };
          img.src = url;
        }
      })
    );
    this.subscription.add(
      this.mainLayoutService.user$.subscribe(user => {
        this.user = user;
        if (user) {
          this.authService.setUserInSession(user); 
        }
        if (this.user?.userProfiles?.length) {
          const savedProfileId = localStorage.getItem('selectedProfileId');
          if (savedProfileId) {
            const found = this.user.userProfiles.find((p: any) => String(p.userProfileId) === savedProfileId);
            this.selectedProfile = found ?? this.user.userProfiles.reduce((prev: any, curr: any) => prev.userProfileId < curr.userProfileId ? prev : curr);
          } else {
            this.selectedProfile = this.user.userProfiles.reduce((prev: any, curr: any) => prev.userProfileId < curr.userProfileId ? prev : curr);
          }
          this.mainLayoutService.setActiveProfile(this.selectedProfile?.profile ?? null);
        }
      })
    );
    this.subscription.add(
      this.mainLayoutService.impersonation$.subscribe(impersonation => {
        this.impersonation = impersonation;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  onSidebarCollapseToggle(event: MouseEvent) {
    event.preventDefault();

    const body = document.body;
    const isMobile = window.innerWidth <= 992;

    if (isMobile) {
      if (body.classList.contains('sidebar-open')) {
        this.renderer.removeClass(body, 'sidebar-open');
      } else {
        this.renderer.removeClass(body, 'sidebar-collapse');
        this.renderer.addClass(body, 'sidebar-open');
      }
    } else if (body.classList.contains('sidebar-collapse')) {
      this.renderer.removeClass(body, 'sidebar-collapse');
      this.renderer.removeClass(body, 'sidebar-mini');
    } else {
      this.renderer.addClass(body, 'sidebar-collapse');
      this.renderer.addClass(body, 'sidebar-mini');
    }
  }

  goToProfile(): void {
    if (this.user?.userId) {
      this.routeService.userUpdate(this.user.userId);
    }
  }

  SignOut() {
    localStorage.clear(); 
    this.router.navigate(['/login']).then(() => {
      location.reload(); 
    });
  }

  returnToAdmin(): void {
    if (!this.impersonation) {
      return;
    }

    this.storeService.setAuthToken(this.impersonation.auth);

    this.authService.impersonate(this.impersonation.userId).subscribe({
      next: (response: any) => {
        if (response.status.code !== 200) {
          const errorMessage = response.status.error || 'Unable to return to your account.';
          this.alertNotificationService.error(errorMessage);
          return;
        }

        this.storeService.setAuthToken(response.data.auth);

        this.userService.getMe().subscribe({
          next: (userResponse: any) => {
            if (userResponse.status.code === 200) {
              const user = userResponse?.data?.user;
              this.storeService.setUserData(user);
              this.mainLayoutService.setUser(user);
              localStorage.setItem('user', JSON.stringify(user));
              this.mainLayoutService.refreshCompany();
              this.mainLayoutService.endImpersonation();

              this.alertNotificationService.success('You are back on your own account.');
              this.router.navigate(['/app']);
            }
          },
          error: (error) => {
            const errorMessage = error?.error?.error?.description || 'Failed to fetch your account data.';
            this.alertNotificationService.error(errorMessage);
          }
        });
      },
      error: (error) => {
        const errorMessage = error?.error?.error?.description || 'Unable to return to your account.';
        this.alertNotificationService.error(errorMessage);
      }
    });
  }

  selectProfile(profile: any): void {
    this.usersProfileService.changeUserProfileStatus(profile.userProfileId, 'active').subscribe({
      next: (response: any) => {
        this.selectedProfile = profile;

        localStorage.setItem('selectedProfileId', String(profile.userProfileId));

        if (response.data?.auth) {
          this.storeService.setAuthToken(response.data.auth);
        }

        if (response.data?.user) {
          this.mainLayoutService.setUser(response.data.user);
          this.authService.setUserInSession(response.data.user);
          localStorage.setItem('user', JSON.stringify(response.data.user));
        }

        this.mainLayoutService.setActiveProfile(this.selectedProfile?.profile ?? null);
        this.alertNotificationService.success('User profile successfully changed');

        this.mainLayoutService.refreshCompany();
      },
      error: (error: any) => {
        console.error('Error changing user profile:', error);
        this.alertNotificationService.error('Unable to change user profile');
      }
    });
  }
}
